// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/GrowthScoreContext.tsx (ADAPT)
// ADAPTED (plan P4-1, Step 4.2): TP's provider holds a mutable session (the Match weights, the cohort calibration, the
// grades, tracked brands, the agents started) and BUILDS the assessment from fixtures. Ours holds nothing to mutate: the
// view model is the BFF's assessment (`useAssessment`), the brand guide and the browser's confirmed brand profile, mapped
// in one place (`toGrowthScoreVm`). The calibration state, `teamMode`, `owner` and `started` are not ported.
/**
 * The Growth Score view model: what every screen of the assessment page reads. It lives as long as the page does.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'

import type { OnboardingSessionState } from '@/features/onboarding-chat/state/session'
import {
  useAssessment,
  useBrandGuide,
  useCompetitors,
  useRefetchAssessmentWhenFunnelSettles,
} from '@/features/onboarding-chat/hooks/useOnboardingQueries'
import type { RecoverSession } from '@/features/onboarding-chat/hooks/useSessionRecovery'

import { toGrowthScoreVm, type GrowthScoreVm } from './growth-score-view'

export type { GrowthScoreVm } from './growth-score-view'

const Ctx = createContext<GrowthScoreVm | null>(null)

export function GrowthScoreProvider({ vm, children }: { vm: GrowthScoreVm; children: ReactNode }) {
  return <Ctx.Provider value={vm}>{children}</Ctx.Provider>
}

export function useGrowthScore(): GrowthScoreVm {
  const vm = useContext(Ctx)
  if (!vm) throw new Error('useGrowthScore must be used inside <GrowthScoreProvider>')
  return vm
}

export type GrowthScoreModel =
  | { status: 'loading' }
  | { status: 'not_ready' }
  | { status: 'error' }
  | { status: 'ready'; vm: GrowthScoreVm }

/**
 * Reads the assessment (read again once when the funnel job settles), the brand guide and takes the browser session,
 * and maps them to the view model. A guide that cannot be read leaves the brand's name and logo to the site's address
 * and the initial: it never blocks the page. `not_ready` (the quiz is not stored on the BFF) is its own status.
 */
export function useGrowthScoreModel({
  sessionId,
  session,
  recover,
}: {
  sessionId: string
  session: Pick<OnboardingSessionState, 'url' | 'answers' | 'brandProfile'>
  recover?: RecoverSession
}): GrowthScoreModel {
  const assessment = useAssessment(sessionId, true, recover)
  useRefetchAssessmentWhenFunnelSettles(sessionId, true, recover)
  const guide = useBrandGuide(sessionId, true, null, recover).data ?? null
  // Whether the person skipped the competitors step: an empty cohort alone cannot say it (the list may just not have been read).
  const competitors = useCompetitors(sessionId, true, recover)
  const confirmation = competitors.state === 'ready' ? competitors.data.confirmation : undefined
  const competitorsSkipped = confirmation !== undefined && (confirmation.skipped || confirmation.brandIds.length === 0)
  const data = assessment.state === 'ready' ? assessment.data : null
  const vm = useMemo(
    () => (data ? toGrowthScoreVm({ assessment: data, session, guide, competitorsSkipped }) : null),
    [data, session, guide, competitorsSkipped],
  )
  if (vm) return { status: 'ready', vm }
  return assessment.state === 'not_ready' ? { status: 'not_ready' } : assessment.state === 'error' ? { status: 'error' } : { status: 'loading' }
}
